import { normalizeSearch, tableauDisplayTitle } from "./displayHelpers";
import type {
  Game,
  PlayerDirectory,
  Pool,
  ResultsDoc,
  Round,
  SpiddParticipant,
} from "./types";

export interface PlayerSearchHit {
  kind: "player";
  licence: string;
  displayName: string;
  club: string | null;
  points: number | null;
  tableauId: string;
  tableauLabel: string;
  poolId: string | null;
  poolLabel: string | null;
  rank: number | null;
  qualification?: string;
}

export interface ClubSearchHit {
  kind: "club";
  clubKey: string;
  clubLabel: string;
  players: PlayerSearchHit[];
}

export type SearchHit = PlayerSearchHit | ClubSearchHit;

function participantClubLabel(p: SpiddParticipant): string | null {
  return (
    (p.clubCourt ?? "").trim() ||
    (p.clubLong ?? "").trim() ||
    (p.clubNumero ?? "").trim() ||
    null
  );
}

function findPoolForLicence(
  pools: Pool[],
  licence: string
): { pool: Pool; rank: number | null; qualification?: string } | null {
  for (const pool of pools) {
    const standing = pool.standings.find((s) => s.licence === licence);
    if (standing) {
      return {
        pool,
        rank: standing.rank,
        qualification: standing.qualification,
      };
    }
  }
  return null;
}

export function collectPlayerMatches(
  doc: ResultsDoc,
  licence: string
): { poolMatches: Game[]; knockoutMatches: Game[] } {
  const poolMatches = doc.pools.flatMap((p) =>
    p.matches.filter((m) => m.players.includes(licence))
  );
  const knockoutMatches = doc.rounds.flatMap((r) =>
    r.matches.filter((m) => m.players.includes(licence))
  );
  return { poolMatches, knockoutMatches };
}

/** Parcours KO : ids des matchs où le joueur apparaît. */
export function playerKnockoutPath(
  rounds: Round[],
  licence: string
): Set<string> {
  const ids = new Set<string>();
  for (const round of rounds) {
    for (const match of round.matches) {
      if (match.players.includes(licence)) ids.add(match.id);
    }
  }
  return ids;
}

const CONFIRMED_KO_STATUSES = new Set([
  "TERMINE",
  "EXEMPT",
  "FORFAIT",
  "ABANDON",
  "EN_COURS",
]);

export type JourneyStepKind =
  | "pool"
  | "qualification"
  | "knockout"
  | "exempt";

export interface JourneyStep {
  id: string;
  kind: JourneyStepKind;
  label: string;
  detail?: string | null;
  game?: Game | null;
  roundId?: string | null;
  roundLabel?: string | null;
  confirmed: boolean;
}

export interface PlayerJourney {
  licence: string;
  poolId: string | null;
  poolLabel: string | null;
  rank: number | null;
  qualification?: string;
  poolMatches: Game[];
  knockoutMatches: Game[];
  steps: JourneyStep[];
  /** Dernier tour KO confirmé sportivement (pas un simple slot TBD). */
  furthestRoundLabel: string | null;
}

function opponentLicence(game: Game, licence: string): string | null {
  const other = game.players.find((p) => p && p !== licence);
  return other ?? null;
}

/**
 * Construit le parcours d’un joueur sans inventer d’avancement sportif.
 * Un slot KO avec statut A_JOUER et adversaire null n’est pas un tour « atteint ».
 */
export function buildPlayerJourney(
  doc: ResultsDoc,
  licence: string,
  directory: PlayerDirectory = {}
): PlayerJourney | null {
  const poolInfo = findPoolForLicence(doc.pools, licence);
  const { poolMatches, knockoutMatches } = collectPlayerMatches(doc, licence);
  const inParticipants = (doc.participants ?? []).some(
    (p) => p.licence === licence
  );
  if (!poolInfo && knockoutMatches.length === 0 && !inParticipants) {
    return null;
  }

  const steps: JourneyStep[] = [];

  if (poolInfo) {
    steps.push({
      id: `pool-${poolInfo.pool.id}`,
      kind: "pool",
      label: poolInfo.pool.label,
      detail:
        poolInfo.rank != null ? `Rang ${poolInfo.rank}` : "Classement en cours",
      confirmed: true,
    });
  }

  if (poolInfo?.qualification === "QUALIFIED") {
    steps.push({
      id: `qual-${licence}`,
      kind: "qualification",
      label: "Qualifié",
      detail: "Tableau final",
      confirmed: true,
    });
  } else if (poolInfo?.qualification === "ELIMINATED") {
    steps.push({
      id: `elim-${licence}`,
      kind: "qualification",
      label: "Éliminé",
      detail: "Non qualifié pour le tableau final",
      confirmed: true,
    });
  } else if (poolInfo?.qualification === "PENDING") {
    steps.push({
      id: `pending-${licence}`,
      kind: "qualification",
      label: "Qualification en attente",
      detail: null,
      confirmed: false,
    });
  }

  const orderedRounds = [...doc.rounds].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0)
  );

  let furthestRoundLabel: string | null = null;

  for (const round of orderedRounds) {
    for (const match of round.matches) {
      if (!match.players.includes(licence)) continue;
      const confirmed = CONFIRMED_KO_STATUSES.has(match.status);
      const isExempt = match.status === "EXEMPT";
      const opp = opponentLicence(match, licence);
      const oppName = opp
        ? directory[opp]?.displayName ?? `Licence ${opp}`
        : null;
      steps.push({
        id: `ko-${match.id}`,
        kind: isExempt ? "exempt" : "knockout",
        label: isExempt ? "Exempt" : round.label,
        detail: isExempt
          ? round.label
          : oppName
            ? `vs ${oppName}`
            : match.status === "A_JOUER"
              ? "À jouer"
              : null,
        game: match,
        roundId: round.id,
        roundLabel: round.label,
        confirmed,
      });
      if (confirmed) furthestRoundLabel = round.label;
    }
  }

  return {
    licence,
    poolId: poolInfo?.pool.id ?? null,
    poolLabel: poolInfo?.pool.label ?? null,
    rank: poolInfo?.rank ?? null,
    qualification: poolInfo?.qualification,
    poolMatches,
    knockoutMatches,
    steps,
    furthestRoundLabel,
  };
}

export function searchResultsDocs(
  docs: ResultsDoc[],
  rawQuery: string,
  directory: PlayerDirectory,
  boards: { name: string; label: string }[] = []
): SearchHit[] {
  const q = normalizeSearch(rawQuery);
  if (!q) return [];

  const playerHits: PlayerSearchHit[] = [];
  const clubMap = new Map<string, ClubSearchHit>();

  for (const doc of docs) {
    const resolvedTableauLabel = tableauDisplayTitle(doc, boards);
    for (const participant of doc.participants ?? []) {
      const identity = directory[participant.licence];
      const displayName =
        identity?.displayName ||
        [participant.prenom, participant.nom].filter(Boolean).join(" ") ||
        `Licence ${participant.licence}`;
      const club = participantClubLabel(participant);
      const tokens = [
        participant.licence,
        participant.nom,
        participant.prenom,
        displayName,
        [participant.nom, participant.prenom].filter(Boolean).join(" "),
        [participant.prenom, participant.nom].filter(Boolean).join(" "),
        club,
        participant.clubLong,
        participant.clubCourt,
        participant.clubNumero,
      ]
        .filter(Boolean)
        .map((v) => normalizeSearch(String(v)));

      if (!tokens.some((t) => t.includes(q))) continue;

      const poolInfo = findPoolForLicence(doc.pools, participant.licence);
      const hit: PlayerSearchHit = {
        kind: "player",
        licence: participant.licence,
        displayName,
        club,
        points: participant.points,
        tableauId: doc.sourceTableauId,
        tableauLabel: resolvedTableauLabel,
        poolId: poolInfo?.pool.id ?? null,
        poolLabel: poolInfo?.pool.label ?? null,
        rank: poolInfo?.rank ?? null,
        qualification: poolInfo?.qualification,
      };
      playerHits.push(hit);

      const clubMatched =
        normalizeSearch(club ?? "").includes(q) ||
        normalizeSearch(participant.clubNumero ?? "").includes(q) ||
        normalizeSearch(participant.clubLong ?? "").includes(q);
      if (club && clubMatched) {
        const clubKey = normalizeSearch(club);
        const existing = clubMap.get(clubKey);
        if (existing) {
          if (!existing.players.some((p) => p.licence === hit.licence)) {
            existing.players.push(hit);
          }
        } else {
          clubMap.set(clubKey, {
            kind: "club",
            clubKey,
            clubLabel: club,
            players: [hit],
          });
        }
      }
    }
  }

  const clubHits = Array.from(clubMap.values());
  const playerOnly = playerHits.filter(
    (p) =>
      !clubHits.some(
        (c) =>
          normalizeSearch(c.clubLabel) === normalizeSearch(p.club ?? "") &&
          normalizeSearch(c.clubLabel).includes(q)
      )
  );

  return [...clubHits, ...playerOnly].slice(0, 40);
}
